import { DateFormatter, getMaxDateForAge } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { MIN_AGE_REQUIRED } from '@repo/toolkit/min-age-required'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { GenderEnum } from '../../../lib/types/enums/common/gender.enum'
import { LanguageEnum } from '../../../lib/types/enums/languages/language.enum'
import { LanguageLevelEnum } from '../../../lib/types/enums/languages/language-level.enum'
import { DiplomaEnum } from '../../../lib/types/enums/staff-application/diploma.enum'
import { EducationLevelEnum } from '../../../lib/types/enums/staff-application/education-level.enum'
import { EvalutationEnum } from '../../../lib/types/enums/staff-application/evalutation.enum'
import { MaritalStatusEnum } from '../../../lib/types/enums/staff-application/marital-status.enum'
import { NationalEducationStatusEnum } from '../../../lib/types/enums/staff-application/national-education-status.enum'
import { PriorityEnum } from '../../../lib/types/enums/staff-application/priority.enum'
import { SchoolLevelTaughtEnum } from '../../../lib/types/enums/staff-application/school-level-taught.enum'
import { StaffApplicationLanguageEnum } from '../../../lib/types/enums/staff-application/staff-application-language.enum'
import { StaffApplicationStatusEnum } from '../../../lib/types/enums/staff-application/staff-application-status.enum'
import { StaffApplicationType } from '../../../lib/types/enums/staff-application/staff-application-type.enum'
import { ScoreCard } from '../../../lib/types/models/score-card/score-card'
import { ScoreCardInput } from '../../../lib/types/models/score-card/score-card-input'
import { StaffApplication } from '../../../lib/types/models/staff-application/staff-application'
import { StaffApplicationInput } from '../../../lib/types/models/staff-application/staff-application-input'
import { NullableTransformer } from '../../../lib/types/utils'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../lib/utils/validations/enum-validation'
import { FRENCH_SSN_SCHEMA } from '../../../lib/utils/validations/french-social-security-number'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

// Counts characters like the backend's MaxLength (validator.js isLength), so emoji-heavy text is not
// blocked early: an emoji (surrogate pair) counts as 1, a VS15/VS16 selector as 0
const countBackendLength = (value: string) =>
  value.length -
  (value.match(/[^\uFE0F\uFE0E][\uFE0F\uFE0E]/g)?.length ?? 0) -
  (value.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g)?.length ?? 0)

export const staffApplicationFormSchema: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<NullableTransformer<StaffApplicationInput>> = (t) =>
  z.object({
    status: createEnumSchema({ fieldName: 'statut', enumType: StaffApplicationStatusEnum }),
    type: createEnumSchema({ fieldName: 'type', enumType: StaffApplicationType }),
    language: createEnumSchema({ fieldName: 'langue', enumType: StaffApplicationLanguageEnum }),
    order: createNumberSchema('ordre', { nullish: true }),
    priority: createEnumSchema({ fieldName: 'priorité', enumType: PriorityEnum }),
    automaticEvaluation: createEnumSchema({
      fieldName: 'Évaluation automatique',
      enumType: EvalutationEnum
    }),
    gender: createEnumSchema({ fieldName: 'genre', enumType: GenderEnum }),
    firstName: createStringSchema('prénom'),
    lastName: createStringSchema('NOM'),
    email: createStringSchema('adresse email', { isEmail: true }),
    phoneNumber: PHONE_NUMBER_SCHEMA(t),
    address1: createStringSchema('adresse'),
    address2: createStringSchema('complément d’adresse').nullish(),
    city: createStringSchema('Ville'),
    postalCode: createStringSchema('code postal'),
    country: createStringSchema('pays'),
    birthDate: createDateStringSchema({
      inputName: 'date de naissance',
      maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(MIN_AGE_REQUIRED)),
      errorMessage: `Âge minimum requis: ${MIN_AGE_REQUIRED} ans`
    }),
    birthPlace: createStringSchema('lieu de naissance'),
    birthDepartment: createStringSchema('département de naissance', { nullish: true }),
    birthCity: createStringSchema('ville de naissance', { nullish: true }),
    nationality: createStringSchema('nationalité'),
    partOfTheEuropeanUnion: z.boolean({
      required_error: 'Veuillez choisir une option'
    }),
    maritalStatus: createEnumSchema({
      fieldName: 'statut marital',
      enumType: MaritalStatusEnum,
      nullish: true
    }),
    numberOfChildren: createNumberSchema("nombre d'enfants", {
      isInteger: true,
      nullish: true
    }),
    socialSecurityNumber: FRENCH_SSN_SCHEMA,
    hasComplemenaryInsurance: z.boolean({
      required_error: 'Veuillez choisir une option'
    }),
    complementaryInsuranceName: createStringSchema('NOM de l’assurance complémentaire', {
      nullish: true
    }),
    levelOfEducation: createEnumSchema({
      fieldName: "niveau d'éducation",
      enumType: EducationLevelEnum,
      nullish: true
    }),
    currentDiploma: createStringSchema('diplôme actuel', { nullish: true }),
    yearsOfExperience: createNumberSchema('années d’expérience', {
      isPositive: true,
      nullish: true
    }),
    diplomas: createEnumArraySchema({ fieldName: 'diplôme', enumType: DiplomaEnum })
      .nullish()
      .default([]),
    otherDiploma: createStringSchema('autre diplôme', { nullish: true }),
    memberOfNationalEducationStatus: createEnumSchema({
      fieldName: 'Statut',
      enumType: NationalEducationStatusEnum,
      nullish: true
    }),
    memberOfNationalEducationDate: createDateStringSchema({ inputName: 'Date' }).nullish(),
    hobbiesId: createNumberArraySchema('loisirs', { nullish: true }).default([]),
    currentSituation: createStringSchema('situation actuelle', { nullish: true }),
    currentStudiedDomain: createStringSchema('domaine d’étude actuel', { nullish: true }),
    classLevels: createEnumArraySchema({ fieldName: 'diveau', enumType: SchoolLevelTaughtEnum })
      .nullish()
      .default([]),
    hasSuperVisionExperienceOverSevenDays: z.boolean({
      required_error: 'Veuillez choisir une option'
    }),
    languagesTaught: z
      .array(
        z.object({
          language: createEnumSchema({ fieldName: 'langue', enumType: LanguageEnum }),
          level: createEnumSchema({ fieldName: 'niveau', enumType: LanguageLevelEnum })
        })
      )
      .nullish()
      .default([]),
    countriesWishList: createNumberArraySchema('pays').default([]),
    periods: createNumberArraySchema('periodes', { nullish: true }).default([]),
    motivation: createStringSchema('motivation').refine(
      (value) => countBackendLength(value) <= 5000,
      { message: t('errors.string.max', { fieldName: 'motivation' }) }
    ),
    experience: createStringSchema('expérience').refine(
      (value) => countBackendLength(value) <= 5000,
      { message: t('errors.string.max', { fieldName: 'expérience' }) }
    ),
    comments: createStringSchema('commentaires', { nullish: true }),
    howDidYouHearAboutUsOptionId: z.number().nullish()
  })

export const mapStaffApplicationToInput = (
  staffApplication: StaffApplication
): StaffApplicationInput => {
  const {
    pcr: _pcr,
    pcrScoreCard: _pcrScoreCard,
    hobbies,
    periods,
    languagesTaught,
    countriesWishList,
    ...data
  } = staffApplication

  return {
    ...data,
    periods: periods.map((a) => a.id),
    languagesTaught: languagesTaught.map((l) => ({
      language: l.language,
      level: l.level
    })),
    countriesWishList: countriesWishList.map((c) => c.id),
    hobbiesId: hobbies.map((h) => h.id)
  }
}

export const mapScoreCardToInput = (scoreCard: ScoreCard): ScoreCardInput => {
  const {
    id: _id,
    application,
    hobbies,
    videoId: _videoId,
    transcriptId: _transcriptId,
    applicationId: _applicationId,
    pcr: _pcr,
    pcrId: _pcrId,
    paidToPCR: _paidToPCR,
    contactedViaEmail: _contactedViaEmail,
    contactedViaPhone: _contactedViaPhone,
    current: _current,
    ...data
  } = scoreCard

  return {
    ...data,
    email: application.email,
    phoneNumber: application.phoneNumber,
    hobbies: hobbies.map((hobby) => hobby.id),
    levelOfLanguages: data.levelOfLanguages.map((l) => ({
      language: l.language,
      level: l.level
    })),
    // if the status of the application is one of the following:
    // - FIRST_CONTACT_ESTABLISHED
    // - SECOND_CONTACT_ESTABLISHED
    // - THIRD_CONTACT_ESTABLISHED
    // and the ContactDate is undefined, set the current date
    firstContactDate:
      application.status === StaffApplicationStatusEnum.FIRST_CONTACT_ESTABLISHED &&
      !data.firstContactDate
        ? DateFormatter.toDateFormatted(new Date())
        : data.firstContactDate,
    secondContactDate:
      application.status === StaffApplicationStatusEnum.SECOND_CONTACT_ESTABLISHED &&
      !data.secondContactDate
        ? DateFormatter.toDateFormatted(new Date())
        : data.secondContactDate,
    thirdContactDate:
      application.status === StaffApplicationStatusEnum.THIRD_CONTACT_ESTABLISHED &&
      !data.thirdContactDate
        ? DateFormatter.toDateFormatted(new Date())
        : data.thirdContactDate
  }
}

/**
 * get the previous tab from the search params
 * @returns the previous tab or undefined if it is not a number
 */
export function getPreviousTab() {
  const newSearchParams = new URLSearchParams(location.search)
  const previousTab = newSearchParams.get('previous-tab')
  return previousTab && !isNaN(Number(previousTab)) ? Number(previousTab) : undefined
}

export const staffApplicationStatusesIndexMapping: Record<
  StaffApplicationStatusEnum,
  number | undefined
> = {
  [StaffApplicationStatusEnum.TO_BE_ASSIGNED_TO_PCR]: undefined,
  [StaffApplicationStatusEnum.TO_BE_CONTACTED]: undefined,
  [StaffApplicationStatusEnum.REJECTED_BEFORE_INTERVIEW]: undefined,
  [StaffApplicationStatusEnum.REJECTED_AFTER_INTERVIEW]: undefined,
  [StaffApplicationStatusEnum.ACCEPTED]: undefined,
  [StaffApplicationStatusEnum.CLASSIFIED]: undefined,
  [StaffApplicationStatusEnum.FIRST_CONTACT_ESTABLISHED]: 0,
  [StaffApplicationStatusEnum.SECOND_CONTACT_ESTABLISHED]: 0,
  [StaffApplicationStatusEnum.THIRD_CONTACT_ESTABLISHED]: 0,
  [StaffApplicationStatusEnum.INTERVIEW_SCHEDULED]: 1,
  [StaffApplicationStatusEnum.INTERVIEW_DONE]: 3,
  [StaffApplicationStatusEnum.STAND_BY]: 3
}

export const contactSectionMapping = {
  [StaffApplicationStatusEnum.FIRST_CONTACT_ESTABLISHED]: 'first-contact-section',
  [StaffApplicationStatusEnum.SECOND_CONTACT_ESTABLISHED]: 'second-contact-section',
  [StaffApplicationStatusEnum.THIRD_CONTACT_ESTABLISHED]: 'third-contact-section'
}
